'use client'

import { useCallback, useEffect, useState } from 'react'
import { ImageIcon, Loader2, Search } from 'lucide-react'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'
import type { SiteSearchResponse, SiteSearchType } from '@/lib/unified-site-search'

const SCOPED_TYPES: SiteSearchType[] = ['talent', 'venues', 'events']

const emptyResults: SiteSearchResponse = {
  talent: [],
  venues: [],
  events: [],
  blogs: [],
  states: [],
  pages: [],
}

function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(t)
  }, [value, delay])

  return debounced
}

function SearchThumb({
  src,
  alt,
  className,
}: {
  src: string | null
  alt: string
  className?: string
}) {
  if (src) {
    return (
      <span
        className={cn(
          'relative size-10 shrink-0 overflow-hidden rounded border border-theme-surface-border bg-muted',
          className,
        )}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={src} alt={alt} className="size-full object-cover" />
      </span>
    )
  }

  return (
    <span
      className={cn(
        'flex size-10 shrink-0 items-center justify-center rounded border border-theme-surface-border bg-muted text-muted-foreground',
        className,
      )}
      aria-hidden
    >
      <ImageIcon className="size-4 opacity-70" />
    </span>
  )
}

type EventsPageSearchButtonProps = {
  triggerClassName?: string
  /** Aria label for the trigger button. */
  triggerAriaLabel?: string
  /** Where to navigate when clicking an event result. Defaults to /events/{id}. */
  buildEventHref?: (eventId: string) => string
}

export function EventsPageSearchButton({
  triggerClassName,
  triggerAriaLabel = 'Search events, talent, and venues',
  buildEventHref,
}: EventsPageSearchButtonProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const debounced = useDebouncedValue(query.trim(), 550)
  const [data, setData] = useState<SiteSearchResponse>(emptyResults)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (q: string, signal: AbortSignal) => {
    if (!q) {
      setData(emptyResults)
      setError(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ q, types: SCOPED_TYPES.join(',') })
      const res = await fetch(`/api/site-search?${params.toString()}`, { signal })
      const json = (await res.json()) as SiteSearchResponse & { error?: string }
      if (signal.aborted) return
      if (!res.ok) {
        setData(emptyResults)
        setError(json.error || 'Search failed.')
        return
      }
      setData({
        talent: json.talent ?? [],
        venues: json.venues ?? [],
        events: json.events ?? [],
        blogs: [],
        states: [],
        pages: [],
      })
    } catch (e) {
      if ((e as Error).name === 'AbortError') return
      setData(emptyResults)
      setError('Search failed.')
    } finally {
      if (!signal.aborted) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    const controller = new AbortController()
    void load(debounced, controller.signal)
    return () => controller.abort()
  }, [open, debounced, load])

  const close = () => {
    setOpen(false)
    setQuery('')
    setData(emptyResults)
    setError(null)
  }

  const eventHref = (id: string) => (buildEventHref ? buildEventHref(id) : `/events/${id}`)

  const hasResults =
    data.talent.length > 0 || data.venues.length > 0 || data.events.length > 0

  return (
    <>
      <button
        type="button"
        aria-label={triggerAriaLabel}
        onClick={() => setOpen(true)}
        className={cn(triggerClassName)}
      >
        <Search className="size-4 stroke-[2.25]" />
      </button>

      <CommandDialog
        open={open}
        onOpenChange={(next) => {
          if (next) setOpen(true)
          else close()
        }}
        title="Search events, talent, and venues"
        description="Search for events, talent, and venues."
        className="sm:max-w-xl"
        showCloseButton
      >
        <CommandInput
          placeholder="Search events, talent, venues..."
          value={query}
          onValueChange={setQuery}
          className="border-0"
        />
        <CommandList className="max-h-[min(70vh,420px)]">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Searching…
            </div>
          )}
          {error && !loading && <div className="px-3 py-4 text-sm text-red-600">{error}</div>}
          {!loading && !error && !debounced && (
            <div className="px-3 py-4 text-sm text-muted-foreground">
              Start typing to search events, talent, and venues.
            </div>
          )}
          {!loading && !error && debounced && !hasResults && (
            <CommandEmpty>No results found.</CommandEmpty>
          )}

          {!loading && !error && debounced && hasResults && (
            <>
              {data.talent.length > 0 && (
                <CommandGroup heading="Talent">
                  {data.talent.map((item) => (
                    <CommandItem key={item.id} asChild value={`talent-${item.id}-${item.name}`}>
                      <a
                        href={item.href}
                        className="flex w-full items-center gap-3"
                        onClick={close}
                      >
                        <SearchThumb src={item.imageUrl} alt={item.name} />
                        <span className="min-w-0 flex-1 truncate">{item.name}</span>
                      </a>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {data.events.length > 0 && (
                <CommandGroup heading="Events">
                  {data.events.map((item) => (
                    <CommandItem
                      key={item.id}
                      asChild
                      value={`event-${item.id}-${item.name}-${item.dateLine ?? ''}-${item.subtitle ?? ''}`}
                    >
                      <a
                        href={eventHref(item.id)}
                        className="flex w-full items-center gap-3"
                        onClick={close}
                      >
                        <SearchThumb src={item.imageUrl} alt={item.name} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{item.name}</span>
                          {item.dateLine && (
                            <span className="text-muted-foreground block truncate text-xs">
                              {item.dateLine}
                            </span>
                          )}
                          {item.subtitle && (
                            <span className="text-muted-foreground block truncate text-xs">
                              {item.subtitle}
                            </span>
                          )}
                        </span>
                      </a>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {data.venues.length > 0 && (
                <CommandGroup heading="Venues">
                  {data.venues.map((item) => (
                    <CommandItem key={item.id} asChild value={`venue-${item.id}-${item.name}`}>
                      <a
                        href={`/venues/${item.slug}`}
                        className="flex w-full items-center gap-3"
                        onClick={close}
                      >
                        <SearchThumb src={item.imageUrl} alt={item.name} />
                        <span className="min-w-0 flex-1 truncate">{item.name}</span>
                      </a>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </>
          )}
        </CommandList>
      </CommandDialog>
    </>
  )
}
